import React, { useState, useEffect, useMemo } from 'react';
import DeliverableList from './DeliverableList';
import './PhaseItem.scss';

// 與 DeliverableContent 的 PRESET_ROLES 對齊
const ROLE_ORDER = ['Frontend', '整合', 'Backend', 'QA'];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${parseInt(m)}/${parseInt(d)}`;
};

const PhaseItem = ({ phase, projectId, onEdit, expanded, onToggleExpand }) => {

  // 用 local state 追蹤 deliverables，讓 DeliverableList 的 optimistic update 能即時反映到 header
  const [localDeliverables, setLocalDeliverables] = useState(phase.deliverables || []);

  useEffect(() => {
    setLocalDeliverables(phase.deliverables || []);
  }, [phase.deliverables]);

  const items = localDeliverables.flatMap((d) => d.deliverable_items || []);
  const doneCount = items.filter((i) => i.is_completed).length;
  const totalCount = items.length;
  const percent = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
  const allQaPassed = localDeliverables.length > 0 && localDeliverables.every((d) => d.qa_status === 'success');
  const hasQaFail = localDeliverables.some((d) => d.qa_status === 'fail');

  // 依角色聚合預估與實際時數（跨所有 deliverables）
  const roleHours = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const role = it.role_type || 'Other';
      if (!map.has(role)) map.set(role, { estimated: 0, actual: 0 });
      const agg = map.get(role);
      agg.estimated += parseFloat(it.estimated_hours) || 0;
      agg.actual += parseFloat(it.actual_hours) || 0;
    });
    // 固定順序：先 PRESET_ROLES 內，其他按名稱字母排序
    return Array.from(map, ([role, v]) => ({ role, ...v })).sort((a, b) => {
      const ai = ROLE_ORDER.indexOf(a.role);
      const bi = ROLE_ORDER.indexOf(b.role);
      if (ai === -1 && bi === -1) return a.role.localeCompare(b.role);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [items]);

  const formatHours = (n) => {
    if (n === 0) return '0';
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  };

  const dateRange = phase.start_date || phase.end_date
    ? `${formatDate(phase.start_date) || '?'} ~ ${formatDate(phase.end_date) || '?'}`
    : '';

  const qaClass = hasQaFail ? 'has-qa-fail' : allQaPassed ? 'all-qa-passed' : '';

  return (
    <div className={`phase-item ${expanded ? 'expanded' : ''} ${qaClass}`}>
      <div className="phase-header" onClick={onToggleExpand}>
        <button className="phase-toggle">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`toggle-arrow ${expanded ? 'open' : ''}`}
          >
            <path d="M4 2l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <span className="phase-name">{phase.name}</span>

        {roleHours.length > 0 && (
          <div className="phase-role-hours" onClick={(e) => e.stopPropagation()}>
            {roleHours.map(({ role, estimated, actual }) => (
              <span key={role} className="phase-role-chip">
                <span className="phase-role-label">{role}</span>
                <span className="phase-role-value">
                  預估 {formatHours(estimated)}h / 實際 {formatHours(actual)}h
                </span>
              </span>
            ))}
          </div>
        )}

        {dateRange && <span className="phase-dates">{dateRange}</span>}

        {totalCount > 0 && (
          <span className="phase-progress">{doneCount}/{totalCount}</span>
        )}

        {phase.status === 'completed' && (
          <span className="phase-badge completed">已完成</span>
        )}

        <div className="phase-actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="phase-action-btn"
            onClick={() => onEdit(phase)}
            title="編輯階段"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9.5 2.5l2 2M2 10l-.5 2.5L4 12l7.5-7.5-2-2L2 10z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      {expanded && (
        <div className="phase-body">
          <DeliverableList
            phase={{ ...phase, deliverables: localDeliverables }}
            projectId={projectId}
            onDeliverablesChange={setLocalDeliverables}
          />
        </div>
      )}
    </div>
  );
};

export default PhaseItem;
